package org.example.project.ui.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.example.project.client.api.ActivityApiClient
import org.example.project.client.api.listManagedActivitiesWithSession
import org.example.project.client.api.publishActivityWithSession
import org.example.project.client.error.ApiClientException
import org.example.project.client.error.MissingCsrfTokenException
import org.example.project.client.model.ActivityResponse
import org.example.project.client.model.ActivityStatus
import org.example.project.client.session.SessionRepository
import org.example.project.client.session.SessionState

data class ActivityListUiState(
    val searchQuery: String = "",
    val selectedStatusFilter: ActivityStatus? = null,
    val currentPage: Int = 0,
    val pageSize: Int = 10,
    val activities: List<ActivityResponse> = emptyList(),
    val totalCount: Int = 0,
    val isLoading: Boolean = false,
    val publishConfirmActivity: ActivityResponse? = null,
    val isMutatingDisabled: Boolean = false
) {
    val totalPages: Int
        get() = if (totalCount == 0) 1 else ((totalCount - 1) / pageSize) + 1

    val draftsOnPage: Int
        get() = activities.count { it.status == ActivityStatus.DRAFT }

    val pubOnPage: Int
        get() = activities.count { it.status == ActivityStatus.PUBLISHED }

    val hasFilters: Boolean
        get() = searchQuery.isNotBlank() || selectedStatusFilter != null
}

class ActivityListViewModel(
    private val sessionRepository: SessionRepository,
    private val activityApiClient: ActivityApiClient,
    private val onStatusMessage: ((String) -> Unit)? = null,
    private val onErrorMessage: ((String) -> Unit)? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(
        ActivityListUiState(
            isMutatingDisabled = isMutatingBlocked(sessionRepository.sessionState.value)
        )
    )
    val uiState: StateFlow<ActivityListUiState> = _uiState.asStateFlow()

    init {
        // Sync reauthentication requirement for mutations
        viewModelScope.launch {
            sessionRepository.sessionState.collect { sessionState ->
                val disabled = isMutatingBlocked(sessionState)
                _uiState.update { it.copy(isMutatingDisabled = disabled) }
            }
        }
    }

    private fun isMutatingBlocked(sessionState: SessionState): Boolean {
        return (sessionState as? SessionState.AuthenticatedWeb)?.reauthenticationRequiredForMutation == true
    }

    fun onSearchQueryChange(query: String) {
        _uiState.update { it.copy(searchQuery = query) }
    }

    fun submitSearch() {
        _uiState.update { it.copy(currentPage = 0) }
        loadData()
    }

    fun clearSearch() {
        _uiState.update { it.copy(searchQuery = "", currentPage = 0) }
        loadData()
    }

    fun onStatusFilterSelected(status: ActivityStatus?) {
        _uiState.update { it.copy(selectedStatusFilter = status, currentPage = 0) }
        loadData()
    }

    fun onPreviousPage() {
        val current = _uiState.value.currentPage
        if (current > 0) {
            _uiState.update { it.copy(currentPage = current - 1) }
            loadData()
        }
    }

    fun onNextPage() {
        val current = _uiState.value.currentPage
        val total = _uiState.value.totalPages
        if (current < total - 1) {
            _uiState.update { it.copy(currentPage = current + 1) }
            loadData()
        }
    }

    fun requestPublish(activity: ActivityResponse) {
        _uiState.update { it.copy(publishConfirmActivity = activity) }
    }

    fun dismissPublishDialog() {
        _uiState.update { it.copy(publishConfirmActivity = null) }
    }

    fun confirmPublish(
        onSuccess: ((String) -> Unit)? = null,
        onError: ((String) -> Unit)? = null
    ) {
        val targetActivity = _uiState.value.publishConfirmActivity ?: return
        val targetId = targetActivity.id
        _uiState.update { it.copy(publishConfirmActivity = null) }

        viewModelScope.launch {
            try {
                val published = activityApiClient.publishActivityWithSession(
                    id = targetId,
                    sessionRepository = sessionRepository
                )
                val msg = "Pomyślnie opublikowano zabawę: \"${published.name}\""
                onStatusMessage?.invoke(msg)
                onSuccess?.invoke(msg)
                loadData()
            } catch (e: MissingCsrfTokenException) {
                val err = "Błąd publikacji: wymagana ponowna autoryzacja sesji (CSRF)"
                onErrorMessage?.invoke(err)
                onError?.invoke(err)
            } catch (e: ApiClientException) {
                val err = "Błąd publikacji: ${e.message}"
                onErrorMessage?.invoke(err)
                onError?.invoke(err)
            } catch (e: Throwable) {
                val err = "Błąd połączenia: ${e.message}"
                onErrorMessage?.invoke(err)
                onError?.invoke(err)
            }
        }
    }

    fun loadData() {
        val currentState = _uiState.value
        _uiState.update { it.copy(isLoading = true) }

        viewModelScope.launch {
            try {
                val statusStr = currentState.selectedStatusFilter?.name
                val response = activityApiClient.listManagedActivitiesWithSession(
                    search = currentState.searchQuery.takeIf { it.isNotBlank() },
                    status = statusStr,
                    sort = "newest",
                    limit = currentState.pageSize,
                    offset = currentState.currentPage * currentState.pageSize,
                    sessionRepository = sessionRepository
                )
                _uiState.update { it.copy(activities = response.items, totalCount = response.total) }
            } catch (e: MissingCsrfTokenException) {
                onErrorMessage?.invoke("Wymagana ponowna autoryzacja sesji (ochrona CSRF)")
            } catch (e: ApiClientException) {
                onErrorMessage?.invoke("Błąd pobierania listy: ${e.message}")
            } catch (e: Throwable) {
                onErrorMessage?.invoke("Błąd połączenia: ${e.message}")
            } finally {
                _uiState.update { it.copy(isLoading = false) }
            }
        }
    }
}
